import { msg } from "@lingui/core/macro";
import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { PosTerminal } from "@/features/pos/components/pos-terminal";
import { RestaurantGate } from "@/features/pos/components/restaurant-gate";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Search params del POS — hacen su estado COMPARTIBLE por URL (campaña #9):
 *   - `order`: la orden activa (ticket abierto). Copiar/pegar la URL reabre ese
 *     ticket en otro dispositivo/pestaña; si el id ya no existe o no es accesible
 *     (sync rules), el POS se auto-sana a "Start a new order" (no loader colgado).
 *   - `category`: la pestaña de categoría del grid de productos.
 * Omitimos valores vacíos para mantener las URLs limpias.
 */
interface PosSearch {
  order?: string;
  category?: string;
}

function PosPage() {
  const { t } = useI18n();
  const ctx = useActiveContext();
  const search = useSearch({ from: posRoute.id });
  const navigate = useNavigate();

  return (
    <PageContainer maxWidth="5xl">
      <PageHeader title={t(msg`Point of sale`)} />
      <RestaurantGate ctx={ctx}>
        {(c) => (
          <PosTerminal
            org={c.org}
            branch={c.branch}
            orderParam={() => search().order}
            categoryParam={() => search().category}
            onOrderChange={(order) => {
              void navigate({
                to: "/pos",
                search: (prev: PosSearch) => ({ ...prev, order: order ?? undefined }),
                replace: true,
              });
            }}
            onCategoryChange={(category) => {
              void navigate({
                to: "/pos",
                search: (prev: PosSearch) => ({ ...prev, category: category ?? undefined }),
                replace: true,
              });
            }}
          />
        )}
      </RestaurantGate>
    </PageContainer>
  );
}

export const posRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pos",
  validateSearch: (search: Record<string, unknown>): PosSearch => ({
    order:
      typeof search.order === "string" && search.order.trim() !== "" ? search.order : undefined,
    category:
      typeof search.category === "string" && search.category.trim() !== ""
        ? search.category
        : undefined,
  }),
  component: PosPage,
});
